import { deserializeElementPropertyStringValue, deserializeTextStyle, serializeTextStyle } from './property-value-serializer';
export function deserializeWidgetText(text) {
    try {
        // JSON.parse("null") will be parsed as null
        if (text === 'null') {
            return { text };
        }
        const value = JSON.parse(text);
        if (typeof value === 'number') {
            return { text: text.toString() };
        }
        return value;
    }
    catch (_a) {
        if (typeof text !== 'string') {
            throw new Error(`Could not deserialize widgetText: '${text}'`);
        }
        return { text: text };
    }
}
export function deserializeVersionedText(text) {
    let versionedText;
    // Try to serialize
    try {
        versionedText = JSON.parse(text);
    }
    catch (_a) {
        const msg = `Could not deserialize text: '${text}'`;
        // JSON is provided but could not parse anyway
        if ((text !== undefined && text.charAt(0) === '{') || text.charAt(0) === '<') {
            throw new Error(msg);
        }
        versionedText = { text: text, styles: [] };
    }
    // JSON.parse on a number is valid and does not go into the catch
    // Happens for widget custom text properties with a numeric value in a string e.g "0.85"
    if (typeof versionedText === 'number') {
        versionedText = { text: text, styles: [] };
    }
    if (!versionedText.styles) {
        // unmigrated widgetText
        return {
            text: versionedText.text,
            styles: []
        };
    }
    const remappedSpans = versionedText.styles.map(span => {
        const styleIdMap = {};
        for (const style of span.styleIds) {
            styleIdMap[style.documentId] = style.styleId;
        }
        return Object.assign(Object.assign({}, span), { styleIds: styleIdMap });
    });
    return {
        text: versionedText.text,
        styles: remappedSpans
    };
}
export function serializeStyle(style) {
    const styleText = [];
    for (const prop in style) {
        const property = prop;
        if (property === '__fontFamilyId') {
            continue;
        }
        const value = style[property];
        if (value !== undefined) {
            styleText.push(`${property}:${serializeTextStyle(property, value)}`);
        }
    }
    return styleText.sort().join(';');
}
export function deserializeStyle(text) {
    const properties = text
        .split(';')
        .filter(p => p.includes(':'))
        .map(p => {
        // Handle links which includes 'http://'.
        const propertyLastIndex = p.indexOf(':');
        return [
            p.substring(0, propertyLastIndex),
            p.substring(propertyLastIndex + 1)
        ];
    });
    const style = {};
    for (const property of properties) {
        const [key, value] = property;
        style[key] = deserializeTextStyle(key, value);
    }
    return style;
}
export function deserializeHTMLElementTextStyles(element) {
    const propertiesToUse = element.dataset.studioValues ? element.dataset.studioValues.split(' ') : [];
    const style = {};
    for (const property of propertiesToUse) {
        switch (property) {
            case 'font':
                style.font = deserializeElementPropertyStringValue('font', element.dataset.font);
                break;
            case 'textColor':
                style.textColor = deserializeElementPropertyStringValue('textColor', element.style.color);
                break;
            case 'fontSize':
                style.fontSize = deserializeElementPropertyStringValue('fontSize', element.style.fontSize);
                break;
            case 'strikethrough':
            case 'underline':
                if (element.style.textDecoration.indexOf('line-through') > -1) {
                    style.strikethrough = true;
                }
                if (element.style.textDecoration.indexOf('underline') > -1) {
                    style.underline = true;
                }
                break;
            case 'uppercase':
                style.uppercase = element.style.textTransform.indexOf('uppercase') > -1;
                break;
            case 'characterSpacing':
                style.characterSpacing = deserializeElementPropertyStringValue('characterSpacing', element.style.letterSpacing);
                break;
            case 'lineHeight':
                style.lineHeight = deserializeElementPropertyStringValue('lineHeight', element.style.lineHeight);
                break;
            case 'textShadows':
                style.textShadows = deserializeElementPropertyStringValue('textShadows', element.style.textShadow);
                break;
            case 'variable':
                style.variable = deserializeElementPropertyStringValue('feed', element.dataset.variable);
        }
    }
    return style;
}
export function serializeInlineStyledText(text) {
    const spans = [];
    for (const span of text.styles) {
        if (span.type === "end" /* SpanType.End */) {
            break;
        }
        spans.push({
            type: span.type,
            length: span.length,
            position: span.position,
            style: serializeStyle(span.style)
        });
    }
    return JSON.stringify({ text: text.text, styles: spans });
}
export function serializeVersionedText(text) {
    const spans = [];
    for (const span of text.styles) {
        if (span.type === "end" /* SpanType.End */) {
            break;
        }
        if (span.type === "composition" /* SpanType.Composition */) {
            continue;
        }
        const styleIds = [];
        for (const [documentId, styleId] of Object.entries(span.styleIds)) {
            styleIds.push({ documentId, styleId });
        }
        let variable;
        if (span.type === "variable" /* SpanType.Variable */ && span.variable) {
            variable = Object.assign(Object.assign({}, span.variable), { type: 'text' });
        }
        spans.push({
            type: span.type,
            length: span.length,
            position: span.position,
            styleIds: styleIds.sort((a, b) => {
                if (a.documentId > b.documentId) {
                    return 1;
                }
                else if (a.documentId < b.documentId) {
                    return -1;
                }
                else if (a.styleId > b.styleId) {
                    return 1;
                }
                else if (a.styleId < b.styleId) {
                    return -1;
                }
                throw new Error('StyleId is not unique');
            }),
            variable
        });
    }
    return JSON.stringify({ text: text.text, styles: spans });
}
export function deserializeInlineStyledText(text) {
    const inlineStyledText = JSON.parse(text);
    const remappedSpans = inlineStyledText.styles.map(span => (Object.assign(Object.assign({}, span), { style: deserializeStyle(span.style) })));
    return {
        text: inlineStyledText.text,
        styles: remappedSpans
    };
}
